import React from 'react';
import { Box, Button, Paper, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import { brand } from '../../theme';
import { formatTimecode } from '../../utils/timecode';
import { angleLabel, angleName, findAngle } from './multicamStudioModel';
import { angleColor } from './multicamTheme';

interface CutComposerProps {
  fps: number;
  playheadTime: number;
  angles: Array<{ angleKey: string; label?: string | null }>;
  /** The angle to cut to. */
  target: string | null;
  onTargetChange: (angleKey: string) => void;
  hasFootage: (angleKey: string) => boolean;
  reason: string;
  onReasonChange: (text: string) => void;
  onSubmit: () => void;
  /** Why a cut can't be added at the playhead, or null when it can. */
  blockedReason?: string | null;
  disabled?: boolean;
  /** A cut is being saved. */
  busy?: boolean;
  onFocus?: () => void;
  inputRef?: React.Ref<HTMLInputElement>;
}

/**
 * The camera-cut counterpart of the studio's QuickComposer, docked in the
 * same place: pick the angle to cut to, add an optional reason, Enter to add.
 * The cut lands on the frame at the playhead.
 */
const CutComposer: React.FC<CutComposerProps> = ({
  fps,
  playheadTime,
  angles,
  target,
  onTargetChange,
  hasFootage,
  reason,
  onReasonChange,
  onSubmit,
  blockedReason = null,
  disabled = false,
  busy = false,
  onFocus,
  inputRef,
}) => {
  const [focused, setFocused] = React.useState(false);
  const targetAngle = findAngle(angles, target);
  const timecode = formatTimecode(playheadTime, fps);
  const label = targetAngle ? `Cut to ${angleName(targetAngle)} at ${timecode}` : `Add a cut at ${timecode}`;
  const canSubmit = !disabled && !busy && Boolean(targetAngle) && !blockedReason;

  return (
    <Paper
      variant="outlined"
      data-testid="cut-composer"
      sx={{
        px: { xs: 1.5, md: 1.75 },
        py: 1.5,
        borderRadius: 3,
        borderColor: focused ? brand.copper : brand.border,
        transition: 'border-color 150ms ease',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={target}
          onChange={(_, value: string | null) => value && onTargetChange(value)}
          aria-label="Angle to cut to"
          disabled={disabled}
          sx={{ flexShrink: 0, height: 40, maxWidth: '100%', overflowX: 'auto' }}
        >
          {angles.map((angle) => {
            const available = hasFootage(angle.angleKey);
            const own = angleLabel(angle);
            return (
              <ToggleButton
                key={angle.angleKey}
                value={angle.angleKey}
                disabled={disabled || !available}
                aria-label={`Cut to angle ${angle.angleKey}${own ? `, ${own}` : ''}${available ? '' : ' (no footage here)'}`}
                sx={{ px: 1.25, gap: 0.75, fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: angleColor(angle.angleKey), flexShrink: 0 }} />
                <Box component="span">{angle.angleKey}</Box>
                {own && (
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, maxWidth: 112, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {own}
                  </Box>
                )}
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>
        <TextField
          inputRef={inputRef}
          fullWidth
          size="small"
          label={label}
          placeholder="Why this angle? (optional)"
          value={reason}
          disabled={disabled}
          onFocus={() => {
            setFocused(true);
            onFocus?.();
          }}
          onBlur={() => setFocused(false)}
          onChange={(event) => onReasonChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
              event.preventDefault();
              if (canSubmit) onSubmit();
            } else if (event.key === 'Escape') {
              (event.target as HTMLElement).blur();
            }
          }}
          InputLabelProps={{ sx: { fontVariantNumeric: 'tabular-nums' } }}
          sx={{ minWidth: 200 }}
        />
        <Tooltip title={blockedReason ?? ''}>
          <span>
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={!canSubmit}
              startIcon={<ContentCutOutlinedIcon />}
              sx={{ flexShrink: 0, height: 40, px: 2.25, whiteSpace: 'nowrap' }}
            >
              {busy ? 'Adding…' : 'Add cut'}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {(blockedReason || focused) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25, flexWrap: 'wrap', minHeight: 24 }}>
          {blockedReason && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }} role="status">
              {blockedReason}
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          {focused && (
            <Typography variant="caption" color="text.secondary">
              Enter to add · Esc to go back to the video
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default CutComposer;
